import {
  APIComponentType,
  APIModelAttributes,
  APIWorkspaceAttributes,
  ArdoqId,
  WorkspaceIntegration,
} from '@ardoq/api-types';
import type { EnhancedScopeData } from '@ardoq/data-model';
import { toIds } from '@ardoq/core';

export const getAttribute = <Key extends keyof APIWorkspaceAttributes>(
  scopeData: EnhancedScopeData,
  workspaceId: ArdoqId,
  attribute: Key
): APIWorkspaceAttributes[Key] | null => {
  const workspace = scopeData.workspacesById[workspaceId];
  return workspace ? workspace[attribute] : null;
};

export const getAPIComponentTypes = (
  scopeData: EnhancedScopeData,
  workspaceId: ArdoqId
): APIComponentType[] => {
  const workspace = scopeData.workspacesById[workspaceId];
  const model = scopeData.modelsById[workspace.componentModel];
  if (!model) return [];
  return Object.values(scopeData.typesByModelId[model._id].componentTypesById);
};

export const isFlexibleWorkspace = (
  scopeData: EnhancedScopeData,
  workspaceId: ArdoqId
): boolean => {
  const model = getWorkspaceModel(scopeData, workspaceId);
  return model ? model.flexible : false;
};

export const getWorkspaceModel = (
  scopeData: EnhancedScopeData,
  workspaceId: ArdoqId
): APIModelAttributes | null => {
  const workspace = scopeData.workspacesById[workspaceId];
  return workspace
    ? (scopeData.modelsById[workspace.componentModel] ?? null)
    : null;
};

export const getRootComponents = (
  scopeData: EnhancedScopeData,
  workspaceId: ArdoqId
) => scopeData.hierarchy.get(workspaceId)?.get(null) ?? [];

export const isWorkspaceEmpty = (
  scopeData: EnhancedScopeData,
  workspaceId: ArdoqId
) => {
  const rootComponents = getRootComponents(scopeData, workspaceId);
  return rootComponents.length === 0;
};

export const isWorkspace = (
  scopeData: EnhancedScopeData,
  workspaceId: ArdoqId
) => Boolean(scopeData.workspacesById[workspaceId]);

export const getWorkspaceIntegrations = (
  scopeData: EnhancedScopeData,
  workspaceId: ArdoqId
): WorkspaceIntegration[] =>
  getAttribute(scopeData, workspaceId, 'integrations') ?? [];

export const isExternallyManaged = (
  scopeDate: EnhancedScopeData,
  workspaceId: ArdoqId
) => {
  const wSIntegrations = getWorkspaceIntegrations(scopeDate, workspaceId);
  return Array.isArray(wSIntegrations) && wSIntegrations.length > 0;
};

export const getAllWorkspaceIds = (scopeData: EnhancedScopeData) =>
  toIds(scopeData.workspaces);

export const getReferenceTypeById = (
  scopeData: EnhancedScopeData,
  workspaceId: ArdoqId,
  referenceTypeId: string | number
) => {
  const workspace = scopeData.workspacesById[workspaceId];
  if (!workspace?.componentModel) return null;
  return (
    scopeData.typesByModelId[workspace.componentModel].referenceTypesById[
      referenceTypeId
    ] ?? null
  );
};
